import { useState, useRef, useEffect, Component } from "react";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Brain, Send, Loader2, User, Sparkles, RefreshCw, Info, AlertTriangle } from "lucide-react";

class CopilotErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(e) { return { error: e }; }
  render() {
    if (this.state.error) {
      return (
        <div className="flex flex-col items-center justify-center h-full text-center gap-4 p-8">
          <AlertTriangle className="w-8 h-8 text-amber-400" />
          <div className="text-zinc-300 font-section">Copilot encountered an error</div>
          <div className="text-xs text-zinc-500 max-w-sm">{String(this.state.error.message)}</div>
          <button onClick={() => this.setState({ error: null })} className="text-xs text-zinc-400 underline">Try again</button>
        </div>
      );
    }
    return this.props.children;
  }
}

const STARTERS = [
  "What are my best performing strategies this week?",
  "Explain the current active signals and which one to act on.",
  "Suggest position sizing for my current portfolio.",
  "Which symbols should I focus on given today's market conditions?",
  "Why did my last trade generate a loss? What can I improve?",
  "Show me my worst drawdown periods and what caused them.",
  "What is the risk on my current open positions?",
  "Find the best Crude Oil strategy for current conditions.",
];

function Message({ msg }) {
  const isUser = msg.role === "user";
  return (
    <div className={`flex gap-3 ${isUser ? "flex-row-reverse" : ""}`}>
      <div className={`w-7 h-7 rounded-sm flex items-center justify-center shrink-0 mt-0.5 ${
        isUser ? "bg-zinc-700" : "bg-white/10"
      }`}>
        {isUser
          ? <User className="w-3.5 h-3.5 text-zinc-300" />
          : <Sparkles className="w-3.5 h-3.5 text-white" />}
      </div>
      <div className={`max-w-[80%] rounded-sm px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
        isUser
          ? "bg-zinc-800 text-zinc-100 ml-auto"
          : "bg-zinc-900 border border-zinc-800 text-zinc-200"
      }`}>
        {msg.content}
      </div>
    </div>
  );
}

export default function Copilot() {
  const [history,     setHistory]     = useState([]);
  const [input,       setInput]       = useState("");
  const [loading,     setLoading]     = useState(false);
  const [ctxSummary,  setCtxSummary]  = useState("");
  const bottomRef = useRef(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [history, loading]);

  async function send(text) {
    const msg = (text || input).trim();
    if (!msg || loading) return;
    setInput("");

    const userMsg = { role: "user", content: msg };
    setHistory((h) => [...h, userMsg]);
    setLoading(true);

    try {
      const { data } = await api.post("/copilot/chat", { message: msg, history });
      const replyText = typeof data.reply === "string" ? data.reply : (data.reply?.text ?? JSON.stringify(data.reply));
      setHistory((h) => [...h, { role: "assistant", content: replyText }]);
      if (data.context_summary) setCtxSummary(data.context_summary);
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message || "Unknown error";
      toast.error(detail);
      setHistory((h) => [...h, {
        role: "assistant",
        content: `Sorry, I couldn't reach the AI service. ${detail}. Check Admin → Health → LLM Ping for details.`,
      }]);
    } finally {
      setLoading(false);
      textareaRef.current?.focus();
    }
  }

  function onKeyDown(e) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  }

  function clearChat() {
    setHistory([]);
    setCtxSummary("");
  }

  const isEmpty = history.length === 0;

  return (
    <CopilotErrorBoundary>
    <AppShell>
      <div className="flex flex-col h-[calc(100vh-48px)]">
        {/* Header */}
        <div className="px-6 py-4 border-b border-zinc-800 flex items-center justify-between shrink-0">
          <PageHeader
            title="AI Copilot"
            subtitle="Your personal trading intelligence assistant"
            icon={<Brain className="w-5 h-5" />}
          />
          <Button
            size="sm"
            variant="ghost"
            onClick={clearChat}
            className="text-zinc-500 hover:text-white"
          >
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
            New Chat
          </Button>
        </div>

        {/* Context bar */}
        {ctxSummary && (
          <div className="px-6 py-2 bg-zinc-900/60 border-b border-zinc-800 flex items-center gap-2 text-xs text-zinc-500 shrink-0">
            <Info className="w-3 h-3 text-zinc-600" />
            <span>Live context: {ctxSummary}</span>
          </div>
        )}

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-5">
          {isEmpty ? (
            <div className="flex flex-col items-center justify-center h-full text-center pb-20">
              <div className="w-14 h-14 bg-white/5 rounded-sm flex items-center justify-center mb-4">
                <Sparkles className="w-7 h-7 text-zinc-300" />
              </div>
              <div className="font-display text-lg text-white mb-1">AlgoForge Copilot</div>
              <div className="text-zinc-500 text-sm max-w-md mb-8">
                Ask me anything about your strategies, signals, P&L, or market conditions.
                I have live access to your portfolio.
              </div>
              <div className="grid grid-cols-2 gap-2 max-w-2xl w-full">
                {STARTERS.map((s) => (
                  <button
                    key={s}
                    onClick={() => send(s)}
                    className="text-left px-3 py-2.5 text-xs text-zinc-400 bg-zinc-900 border border-zinc-800 rounded-sm hover:border-zinc-600 hover:text-zinc-200 transition-colors"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <>
              {history.map((m, i) => <Message key={i} msg={m} />)}
              {loading && (
                <div className="flex gap-3">
                  <div className="w-7 h-7 rounded-sm bg-white/10 flex items-center justify-center shrink-0 mt-0.5">
                    <Sparkles className="w-3.5 h-3.5 text-white" />
                  </div>
                  <div className="bg-zinc-900 border border-zinc-800 rounded-sm px-4 py-3">
                    <Loader2 className="w-4 h-4 animate-spin text-zinc-500" />
                  </div>
                </div>
              )}
              <div ref={bottomRef} />
            </>
          )}
        </div>

        {/* Input */}
        <div className="px-6 py-4 border-t border-zinc-800 shrink-0">
          <div className="flex gap-3 items-end">
            <textarea
              ref={textareaRef}
              rows={2}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Ask about your portfolio, signals, strategies… (Enter to send)"
              className="flex-1 bg-zinc-900 border border-zinc-700 rounded-sm px-3 py-2.5 text-sm text-zinc-100 placeholder-zinc-600 resize-none focus:outline-none focus:border-zinc-500 transition-colors"
            />
            <Button
              size="sm"
              onClick={() => send()}
              disabled={!input.trim() || loading}
              className="bg-white text-black hover:bg-zinc-200 h-[60px] px-4"
            >
              {loading
                ? <Loader2 className="w-4 h-4 animate-spin" />
                : <Send className="w-4 h-4" />}
            </Button>
          </div>
          <div className="mt-1.5 text-xs text-zinc-600">
            Context updates on every message · Shift+Enter for new line
          </div>
        </div>
      </div>
    </AppShell>
    </CopilotErrorBoundary>
  );
}
